"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useLayoutEffect } from "react";

import { useLocale, useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { PropertyTable } from "@/components/ui/SearchTable/components/PropertyTable";
import { DealType } from "@/enums/dealType";
import { FeatureFlags } from "@/enums/feature-flags";
import { EnumViewMode } from "@/enums/view-mode";
import { useUnifiedListing } from "@/hooks/useUnifiedListing";
import { DevelopersApiResponse } from "@/types/property.types";
import { EmptyState } from "@/ui/Common/EmptyState";
import Loader from "@/ui/Common/Loader";
import { ObjectCard } from "@/ui/Common/ObjectCard";
import { ObjectList } from "@/ui/Common/ObjectList";
import { PaginationContainer } from "@/ui/Common/Pagination";
import { EnhancedSearchPanel } from "@/ui/EnhancedSearchPanel";
import { SearchResultHeader } from "@/ui/SearchResultHeader";
import { restoreScroll } from "@/utils/scrollRestoration";
import { titleToSlug } from "@/utils/slug";
import { useUserRole } from "@/hooks/useUserRole";

interface Props {
  values: DevelopersApiResponse;
  h1?: string;
}

export default function RentPage({ values, h1 }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const role = useUserRole();


  // Use unified listing hook - handles all data fetching, pagination, and transformation
  const {
    data,
    transformedData,
    isFetching,
    isInitializing,
    currentPage,
    limit,
    total,
    totalPage,
    setPage,
    setPageSize,
    isEmpty,
    viewMode,
    isRealtor,
    initialFilters,
    localizedLocationString,
  } = useUnifiedListing({
    dealType: DealType.RENT,
    basePath: "/rent",
    values,
  });

  const isLoading = isFetching || !data || isInitializing;

  useLayoutEffect(() => {
    restoreScroll(DealType.RENT);
  }, []);

  return (
    <section className="min-h-screen">
      <EnhancedSearchPanel
        dealType={DealType.RENT}
        initialFilters={initialFilters}
        header={h1 || t("Rent")}
        localizedLocationString={isLoading ? " " : localizedLocationString}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <SearchResultHeader
            total={total}
            dealType={DealType.RENT}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
          <Feature name={FeatureFlags.PROPERTIES_LIST_TABLE_VIEW}>
            <Feature.Enabled>
              {viewMode === EnumViewMode.TABLE && isRealtor ? (
                <PropertyTable
                  properties={transformedData}
                  loading={isFetching}
                  currentSort="date_desc"
                  onSortChange={(sort) => {
                    // Handle sort change
                  }}
                  pagination={{
                    currentPage,
                    pageSize: limit,
                    total,
                    onPageChange: setPage,
                    onPageSizeChange: setPageSize,
                  }}
                  enableBulkActions={true}
                  hideInternalPagination={true}
                />
              ) : (
                <ObjectList isEmpty={isEmpty}>
                  <ObjectList.Items viewMode={viewMode}>
                    {transformedData.map((item: any) => (
                      <ObjectCard
                        key={item.id ?? item.title}
                        id={item.id}
                        title={item.title ?? "N/A"}
                        location={item.location ?? item.city ?? "N/A"}
                        slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                        view={viewMode}
                        pricing={
                          item.pricing || {
                            convertedPrices: item.convertedPrices,
                            convertedPricesPerSqM: item.convertedPricesPerSqM,
                          }
                        }
                        translations={item.translations}
                        canArchive={role.isRealtor}
                        {...item}
                      />
                    ))}
                  </ObjectList.Items>
                  <ObjectList.Empty>
                    <EmptyState />
                  </ObjectList.Empty>
                </ObjectList>
              )}
            </Feature.Enabled>
            <Feature.Disabled>
              <ObjectList isEmpty={isEmpty}>
                <ObjectList.Items viewMode={viewMode}>
                  {transformedData.map((item: any) => (
                    <ObjectCard
                      key={item.id ?? item.title}
                      id={item.id}
                      title={item.title ?? "N/A"}
                      location={item.location ?? item.city ?? "N/A"}
                      slug={item.slug ?? titleToSlug(item.title ?? "n-a")}
                      view={viewMode}
                      pricing={
                        item.pricing || {
                          convertedPrices: item.convertedPrices,
                          convertedPricesPerSqM: item.convertedPricesPerSqM,
                        }
                      }
                      translations={item.translations}
                      canArchive={role.isRealtor}
                      {...item}
                    />
                  ))}
                </ObjectList.Items>
                <ObjectList.Empty>
                  <EmptyState />
                </ObjectList.Empty>
              </ObjectList>
            </Feature.Disabled>
          </Feature>
          {/* External pagination: always when multiple pages */}
          {totalPage > 1 && (
            <PaginationContainer
              totalPage={totalPage}
              currentPage={currentPage}
              pathname={pathname}
              searchParams={searchParams}
            />
          )}
        </>
      )}
    </section>
  );
}
